import type {
  CampaignCreationCapabilities,
  SmartCreationDraft,
  SmartCreationStep,
  SmartCreationStructure,
  SmartCreationStructureEvaluation,
  SmartCreationStructureOptionsEvaluation,
} from "@adpilot/shared";
import { reviewScheduleToday } from "@/components/smart-creation/review-schedule";

export const STEP_ORDER: SmartCreationStep[] = [
  "products",
  "objectives",
  "strategy",
  "structure",
  "preview",
];

export function draftsMatch(left: SmartCreationDraft | null, right: SmartCreationDraft): boolean {
  return left !== null && JSON.stringify(left) === JSON.stringify(right);
}

export function distinctParentCount(draft: SmartCreationDraft): number {
  return new Set(draft.products.map((product) => product.variationGroupKey)).size;
}

export function structureEvaluation(
  options: SmartCreationStructureOptionsEvaluation | undefined,
  structure: SmartCreationStructure,
): SmartCreationStructureEvaluation | undefined {
  const option = options?.[structure];
  return option && "counts" in option ? option : undefined;
}

export function minimumTotalDailyBudget(
  draft: SmartCreationDraft,
  capabilities: CampaignCreationCapabilities,
): number {
  const isolationCount =
    draft.productOrganization === "campaign" ? Math.max(1, distinctParentCount(draft)) : 1;
  return capabilities.dailyBudget.min * isolationCount;
}

export function respectsDecimalPrecision(value: number, decimals: number): boolean {
  const scale = 10 ** decimals;
  return Math.abs(value * scale - Math.round(value * scale)) < 1e-8;
}

export function hasValidPromotionWindow(draft: SmartCreationDraft): boolean {
  if (draft.objective !== "promotion_sales_boost") return true;
  const range = draft.promotionDateRange;
  return Boolean(
    range?.startDate &&
      range.endDate &&
      draft.startDate <= range.startDate &&
      range.startDate <= range.endDate &&
      (!draft.endDate || range.endDate <= draft.endDate) &&
      range.endDate >= reviewScheduleToday(new Date()),
  );
}

export function hasValidCampaignWindow(draft: SmartCreationDraft): boolean {
  return Boolean(
    draft.startDate &&
      draft.startDate >= reviewScheduleToday(new Date()) &&
      (!draft.endDate || draft.endDate >= draft.startDate),
  );
}

export function formatCurrencyValue(value: number | null, currencyCode: string, decimals?: number) {
  if (value === null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      ...(decimals === undefined
        ? {}
        : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
    }).format(value);
  } catch {
    return `${currencyCode} ${value.toFixed(decimals ?? 2)}`;
  }
}
